import { client } from '@/services/client'
import { useSpace } from '@/store/modules/App/space'
import createContentUrl from '@/utils/createContentUrl'
import StoryblokClient from 'storyblok-js-client'
import { sanitizeStoriesToApps } from '@utils/sanitizeStoriesToApps'
import type { App } from '@/types'
import type {
  RecordStrategy,
  RecordStrategyArgs,
  SearchableData,
} from '../types'
import type { AxiosResponse } from 'axios'

type AppWithExternalFlag = App & {
  is_external?: boolean
}

export class AppsRecord implements RecordStrategy {
  async get({ spaceId }: RecordStrategyArgs): Promise<AppWithExternalFlag[]> {
    if (!spaceId) {
      return []
    }
    return await this.loadCombinedApps(spaceId)
  }

  async loadCombinedApps(spaceId: string): Promise<AppWithExternalFlag[]> {
    const apps = await this.loadApps(spaceId)
    const { apps: externalApps } = await this.loadExternalApps()
    return [...externalApps, ...apps]
  }

  async fetchAppsByPage(
    spaceId: string,
    page = 1,
  ): Promise<AxiosResponse<{ apps: App[] }>> {
    const query = `?space_id=${spaceId}&page=${page}`

    return await client.get(`/apps/${query}`, {
      region: useSpace().region,
    })
  }

  async loadApps(spaceId: string): Promise<App[]> {
    try {
      let apps: App[] = []

      const { data, headers } = await this.fetchAppsByPage(spaceId)

      apps = [...data.apps]
      const totalApps = Number(headers.total)
      const pageSize = Number(headers['per-page'])
      const pageCount = Math.ceil(totalApps / pageSize)
      const shouldFetchNextPage = totalApps > pageSize

      if (shouldFetchNextPage) {
        for (let page = 2; page <= pageCount; page++) {
          const { data } = await this.fetchAppsByPage(spaceId, page)
          apps = [...apps, ...data.apps]
        }
      }

      return apps
    } catch (error) {
      console.error('Error fetching apps', error)
      return []
    }
  }

  async loadExternalApps(): Promise<{ apps: App[] }> {
    const contentParam = createContentUrl()
    const sbClient = new StoryblokClient({
      accessToken: contentParam.token,
    })

    try {
      const response = await sbClient.get('cdn/stories/', {
        starts_with: 'external-apps/',
        version: contentParam.version,
        cv: contentParam.cv,
      })

      const sanitizedApps = sanitizeStoriesToApps(
        response.data.stories,
      ) as AppWithExternalFlag[]

      for (const app of sanitizedApps) {
        app.is_external = true
      }

      return {
        apps: sanitizedApps,
      }
    } catch (error) {
      console.error('Error fetching external apps', error)
      return { apps: [] }
    }
  }

  async set(args: RecordStrategyArgs = {}): Promise<void> {
    const { spaceId } = args
    if (!spaceId) {
      return
    }
    const appData = await this.get(args)
    window?.CommandBar?.addRecords(
      'apps',
      this.createSearchableRecord(appData, spaceId),
      {
        descriptionKey: 'description',
      },
    )
  }

  remove(): void {
    window?.CommandBar?.addRecords('apps', [])
  }

  createSearchableRecord(
    records: AppWithExternalFlag[],
    spaceId: string,
  ): SearchableData[] {
    return records.map((item: AppWithExternalFlag) => ({
      spaceId,
      id: item.id,
      title: item.name,
      label: item.name,
      description: item.intro,
      icon: item.icon?.replace(/^\/\//, 'https://'),
      query: item.is_external ? '?external=true' : '',
    }))
  }
}
